/**
 * The unit boundary in the browser.
 *
 * Invariant 5: values are canonical SI internally and units live at the
 * boundaries. The inspector is one of those boundaries, so this module turns
 * "200 µm" into 2e-4 and back, and nothing else in the frontend converts units.
 *
 * Canonical units are the pint expressions templates declare ("meter",
 * "meter ** 3 / second"). Each gets the display units a microfluidics designer
 * would type. An unknown canonical unit still works: it offers itself alone, at
 * a factor of one, rather than guessing a conversion.
 *
 * A bare number is never read as SI by default. The caller always says which
 * unit it is in (the field's unit selector), so a value without a declared unit
 * cannot arise here either.
 */

const MICRO = "\u00b5";

export interface DisplayUnit {
  /** What is shown, e.g. "µm". */
  label: string;
  /** Alternative spellings accepted when typed, lower-cased. */
  aliases: readonly string[];
  /** canonical = value * factor + offset */
  factor: number;
  offset?: number;
  /** Shown only when chosen, never picked by size: L/h sits between mL/min and mL/s. */
  chosenOnly?: boolean;
}

function unit(label: string, factor: number, aliases: string[] = [], offset?: number): DisplayUnit {
  return { label, factor, aliases: [label.toLowerCase(), ...aliases.map((alias) => alias.toLowerCase())], offset };
}

function chosenOnly(display: DisplayUnit): DisplayUnit {
  return { ...display, chosenOnly: true };
}

const MICRO_ALIASES = (suffix: string) => [`u${suffix}`, `\u03bc${suffix}`, `micro${suffix}`];

/** Display units per canonical unit, in order from smallest to largest. */
const UNITS: Readonly<Record<string, readonly DisplayUnit[]>> = {
  meter: [
    unit("nm", 1e-9),
    unit(`${MICRO}m`, 1e-6, [...MICRO_ALIASES("m"), "micron", "microns"]),
    unit("mm", 1e-3),
    unit("cm", 1e-2),
    unit("m", 1, ["meter", "metre"]),
  ],
  "meter ** 2": [
    unit(`${MICRO}m²`, 1e-12, ["um^2", "um2", "µm^2"]),
    unit("mm²", 1e-6, ["mm^2", "mm2"]),
    unit("m²", 1, ["m^2", "m2"]),
  ],
  "meter ** 3": [
    unit("pL", 1e-15, ["pl"]),
    unit("nL", 1e-12, ["nl"]),
    unit(`${MICRO}L`, 1e-9, [...MICRO_ALIASES("l"), "mm^3", "mm³"]),
    unit("mL", 1e-6, ["ml", "cc", "cm^3", "cm³"]),
    unit("L", 1e-3, ["l", "litre", "liter"]),
    unit("m³", 1, ["m^3", "m3"]),
  ],
  "meter ** 3 / second": [
    unit("nL/min", 1e-12 / 60, ["nl/min"]),
    unit("nL/s", 1e-12),
    unit(`${MICRO}L/min`, 1e-9 / 60, MICRO_ALIASES("l/min")),
    unit(`${MICRO}L/s`, 1e-9, MICRO_ALIASES("l/s")),
    unit("mL/min", 1e-6 / 60),
    unit("mL/s", 1e-6),
    unit("L/min", 1e-3 / 60),
    chosenOnly(unit("L/h", 1e-3 / 3600, ["l/hr", "l/hour"])),
    unit("m³/s", 1, ["m^3/s"]),
  ],
  pascal: [
    unit("Pa", 1),
    unit("mbar", 100),
    unit("kPa", 1e3),
    unit("psi", 6894.757293168),
    unit("bar", 1e5),
    unit("MPa", 1e6),
  ],
  second: [unit("ms", 1e-3), unit("s", 1, ["sec"]), unit("min", 60), unit("h", 3600, ["hr"])],
  hertz: [unit("Hz", 1), unit("kHz", 1e3), unit("MHz", 1e6)],
  newton: [unit(`${MICRO}N`, 1e-6, MICRO_ALIASES("n")), unit("mN", 1e-3), unit("N", 1)],
  // Temperature is the one offset conversion. °C is listed first because it is
  // what designers think in; kelvin is what is stored.
  kelvin: [unit("°C", 1, ["c", "degc", "celsius"], 273.15), unit("K", 1, ["kelvin"])],
  radian: [unit("°", Math.PI / 180, ["deg", "degree", "degrees"]), unit("rad", 1, ["radian", "radians"])],
  // Molar mass, for a membrane's molecular weight cutoff: 1 kDa is 1 kg/mol.
  "kilogram / mole": [unit("Da", 1e-3, ["g/mol"]), unit("kDa", 1, ["kg/mol"])],
  // A percentage declared as one, such as a window's transmittance.
  percent: [unit("%", 1, ["percent", "pct"])],
  dimensionless: [unit("", 1, ["-"])],
};

/** The display units offered for a canonical unit. */
export function displayUnits(canonical: string): readonly DisplayUnit[] {
  return UNITS[canonical.trim()] ?? [unit(canonical, 1)];
}

export function toCanonical(value: number, display: DisplayUnit): number {
  return value * display.factor + (display.offset ?? 0);
}

export function fromCanonical(value: number, display: DisplayUnit): number {
  return (value - (display.offset ?? 0)) / display.factor;
}

/**
 * The unit to show a value in: the largest whose magnitude is at least one,
 * so 3e-4 m reads "300 µm" rather than "0.3 mm". Offset units (°C) and angles
 * are preferred outright, being what people say.
 */
export function preferredUnit(canonical: string, value: number | null | undefined): DisplayUnit {
  const units = displayUnits(canonical);
  const first = units[0]!;
  if (first.offset !== undefined || canonical.trim() === "radian") return first;
  if (value === null || value === undefined || value === 0 || !Number.isFinite(value)) {
    return units.find((candidate) => candidate.factor === 1 && candidate.offset === undefined) ?? first;
  }
  const magnitude = Math.abs(value);
  let chosen = first;
  for (const candidate of units) {
    if (!candidate.chosenOnly && magnitude / candidate.factor >= 1 - 1e-9) chosen = candidate;
  }
  return chosen;
}

/** Up to six significant figures, without float noise: 300, not 299.99999999999994. */
export function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  return String(Number(value.toPrecision(6)));
}

/** A canonical value as a person reads it, e.g. "300 µm". */
export function formatQuantity(value: number, canonical: string, display?: DisplayUnit): string {
  const chosen = display ?? preferredUnit(canonical, value);
  const number = formatNumber(fromCanonical(value, chosen));
  return chosen.label ? `${number} ${chosen.label}` : number;
}

export type ParsedQuantity =
  | { ok: true; value: number; unit: DisplayUnit }
  | { ok: false; reason: string };

// Thousands separators are allowed in the whole part, as values are shown with them (D49).
const QUANTITY = /^\s*([+-]?(?:\d{1,3}(?:,\d{3})+(?:\.\d*)?|\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)\s*(.*?)\s*$/;

/**
 * Parse what was typed into a quantity field.
 *
 * "200" is read in `fallback`, the unit the field's selector shows, so the unit
 * is always declared. "0.2 mm" names its own, which must be one this quantity
 * can be expressed in: typing "kPa" into a length is refused, not coerced.
 */
export function parseQuantity(text: string, canonical: string, fallback: DisplayUnit): ParsedQuantity {
  const match = QUANTITY.exec(text);
  if (!match) {
    return { ok: false, reason: `${text.trim() || "An empty value"} is not a number` };
  }
  const magnitude = Number(match[1]!.replace(/,/g, ""));
  const typed = match[2]!.replace(/\s+/g, "");
  let chosen = fallback;
  if (typed) {
    const lower = typed.toLowerCase();
    const found = displayUnits(canonical).find(
      (candidate) => candidate.label === typed || candidate.aliases.includes(lower),
    );
    if (!found) {
      const offered = displayUnits(canonical)
        .map((candidate) => candidate.label)
        .filter(Boolean)
        .join(", ");
      return { ok: false, reason: `${typed} is not a unit of this quantity; use ${offered || "no unit"}` };
    }
    chosen = found;
  }
  return { ok: true, value: toCanonical(magnitude, chosen), unit: chosen };
}

// --- Liquid volumes, flows and lengths (D49) -----------------------------------------

/** How the server names a volume, flow or length unit: ASCII, `uL` for µL. */
export interface DisplayUnitsSetting {
  volume?: string | null;
  flow_rate?: string | null;
  length?: string | null;
}

/**
 * The units a design shows its liquid volumes, flows and lengths in: one each,
 * everywhere. Length may be left unset (null): each length is then shown in the
 * unit that reads best for it, as before lengths could be set.
 */
export interface LiquidUnits {
  volume: DisplayUnit;
  flowRate: DisplayUnit;
  length: DisplayUnit | null;
}

const VOLUME = "meter ** 3";
const FLOW = "meter ** 3 / second";
const LENGTH = "meter";

/** A unit by the server's name for it ("uL/min"), or null if this browser does not know it. */
function byCode(canonical: string, code: string | null | undefined): DisplayUnit | null {
  if (!code) return null;
  return displayUnits(canonical).find((candidate) => candidate.label.replace(MICRO, "u") === code) ?? null;
}

/** A tubing set moves millilitres; a cartridge microlitres. As the server resolves them. */
const KIND_DEFAULTS: Readonly<Record<"tubing_set" | "microfluidic", { volume: string; flow_rate: string }>> = {
  tubing_set: { volume: "mL", flow_rate: "mL/min" },
  microfluidic: { volume: "uL", flow_rate: "uL/min" },
};

/**
 * The liquid units for a design: the server's resolved setting, or its kind's
 * defaults for anything missing or not known here.
 */
export function liquidUnits(
  setting: DisplayUnitsSetting | null | undefined,
  kind: "tubing_set" | "microfluidic" = "tubing_set",
): LiquidUnits {
  const fallback = KIND_DEFAULTS[kind];
  return {
    volume: byCode(VOLUME, setting?.volume) ?? byCode(VOLUME, fallback.volume)!,
    flowRate: byCode(FLOW, setting?.flow_rate) ?? byCode(FLOW, fallback.flow_rate)!,
    length: byCode(LENGTH, setting?.length),
  };
}

/** Where nothing says otherwise: a tubing set's, which the scripts were first written for. */
export const TUBING_SET_UNITS: LiquidUnits = liquidUnits(null, "tubing_set");

/** The units offered for each quantity, as the server names them, for a selector. */
export function liquidUnitChoices(): {
  volume: { code: string; label: string }[];
  flowRate: { code: string; label: string }[];
  length: { code: string; label: string }[];
} {
  const codes = (canonical: string, offered: readonly string[]) =>
    offered.map((code) => ({ code, label: byCode(canonical, code)!.label }));
  return {
    volume: codes(VOLUME, ["nL", "uL", "mL", "L"]),
    flowRate: codes(FLOW, ["nL/s", "nL/min", "uL/s", "uL/min", "mL/s", "mL/min", "L/min", "L/h"]),
    length: codes(LENGTH, ["um", "mm", "cm"]),
  };
}

// en-US grouping, so a 10 L bag reads "10,000 mL" whatever the browser's locale.
const GROUPED = new Intl.NumberFormat("en-US", { maximumSignificantDigits: 6, useGrouping: true });
const COMPACT = new Intl.NumberFormat("en-US", { maximumSignificantDigits: 3, useGrouping: true });

/** A number with thousands separators and up to six significant figures: "10,000". */
export function formatGrouped(value: number, digits: 3 | 6 = 6): string {
  if (!Number.isFinite(value)) return String(value);
  return (digits === 3 ? COMPACT : GROUPED).format(value);
}

/** A volume (m³) in the design's volume unit: "10,000 mL". */
export function volumeIn(volume: number, units: LiquidUnits, digits: 3 | 6 = 6): string {
  return `${formatGrouped(fromCanonical(volume, units.volume), digits)} ${units.volume.label}`;
}

/** A flow (m³/s) in the design's flow unit: "600 mL/min". */
export function flowIn(rate: number, units: LiquidUnits, digits: 3 | 6 = 6): string {
  return `${formatGrouped(fromCanonical(rate, units.flowRate), digits)} ${units.flowRate.label}`;
}

/** The fixed unit a quantity is shown in, where the design sets one; otherwise null. */
export function liquidUnitFor(canonical: string, units: LiquidUnits | null | undefined): DisplayUnit | null {
  if (!units) return null;
  const trimmed = canonical.trim();
  if (trimmed === VOLUME) return units.volume;
  if (trimmed === FLOW) return units.flowRate;
  if (trimmed === LENGTH) return units.length;
  return null;
}

/** A number as typed into a plain field, thousands separators allowed: "10,000" is 10000. */
export function typedNumber(text: string): number {
  return Number(text.trim().replace(/,(?=\d{3}(?:\D|$))/g, ""));
}
